import { useEffect, useMemo, useRef } from "react";

import debounce from "lodash.debounce";

type DebounceOptions = {
  leading?: boolean;
  trailing?: boolean;
  maxWait?: number;
};

type UseDebounceCallbackOptions = DebounceOptions & {
  /** Run a waiting call at unmount instead of dropping it. Autosaves use this to keep the last edit. */
  flushOnUnmount?: boolean;
};

type ControlFunctions = {
  cancel: () => void;
  flush: () => void;
  isPending: () => boolean;
};

export type DebouncedState<T extends (...args: any) => ReturnType<T>> = ((
  ...args: Parameters<T>
) => ReturnType<T> | undefined) &
  ControlFunctions;

/**
 * One debounce timer per component, however often `func` changes: the waiting call runs the
 * latest `func`. A call still waiting at unmount, or when the delay or options change, is
 * dropped unless `flushOnUnmount` is set.
 */
export const useDebounceCallback = <T extends (...args: any) => ReturnType<T>>(
  func: T,
  delay = 500,
  options?: UseDebounceCallbackOptions
): DebouncedState<T> => {
  const funcRef = useRef(func);
  funcRef.current = func;
  const flushOnUnmountRef = useRef(options?.flushOnUnmount ?? false);
  flushOnUnmountRef.current = options?.flushOnUnmount ?? false;

  const leading = options?.leading;
  const trailing = options?.trailing;
  const maxWait = options?.maxWait;

  const debounced = useMemo(() => {
    let pending = false;
    // lodash reads a present key as set, even when undefined, so pass only the given ones.
    const debounceOptions: DebounceOptions = {};
    if (leading !== undefined) debounceOptions.leading = leading;
    if (trailing !== undefined) debounceOptions.trailing = trailing;
    if (maxWait !== undefined) debounceOptions.maxWait = maxWait;
    const debouncedFuncInstance = debounce(
      (...args: Parameters<T>) => {
        pending = false;
        return funcRef.current(...args);
      },
      delay,
      debounceOptions
    );

    const wrappedFunc: DebouncedState<T> = (...args: Parameters<T>) => {
      // Without a trailing edge no later call is coming. A leading call clears this as it runs.
      pending = trailing !== false;
      return debouncedFuncInstance(...args);
    };

    wrappedFunc.cancel = () => {
      pending = false;
      debouncedFuncInstance.cancel();
    };

    wrappedFunc.isPending = () => pending;

    wrappedFunc.flush = () => {
      return debouncedFuncInstance.flush();
    };

    return wrappedFunc;
  }, [delay, leading, trailing, maxWait]);

  useEffect(
    () => () => {
      if (flushOnUnmountRef.current) debounced.flush();
      else debounced.cancel();
    },
    [debounced]
  );

  return debounced;
};
